import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getBrands, createBrand, updateBrand, deleteBrand } from '../api/categories';
import CategoryModal from '../components/CategoryModal';
import CategoryCard from '../components/CategoryCard';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch brands
  const fetchBrands = async () => {
    setLoading(true);
    try {
      const data = await getBrands();
      setBrands(data);
      setFilteredBrands(data);
    } catch (error) {
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredBrands(brands);
    } else {
      const filtered = brands.filter((brand) =>
        brand.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    }
  }, [searchTerm, brands]);

  // Handle create/update with optimistic UI
  const handleSubmit = async (data) => {
    try {
      if (selectedBrand) {
        const updated = await updateBrand(selectedBrand.id, data);
        // Optimistically update local state
        setBrands((prev) =>
          prev.map((b) => (b.id === selectedBrand.id ? { ...b, ...updated } : b))
        );
        toast.success('Brand updated successfully');
      } else {
        const created = await createBrand(data);
        // Optimistically add to local state
        setBrands((prev) => [...prev, created]);
        toast.success('Brand created successfully');
      }
      setShowModal(false);
      setSelectedBrand(null);
    } catch (error) {
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = (brand) => {
    setSelectedBrand(brand);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteBrand(selectedBrand.id);
      // Optimistically remove from local state
      setBrands((prev) => prev.filter((b) => b.id !== selectedBrand.id));
      toast.success('Brand deleted successfully');
      setShowDeleteModal(false);
      setSelectedBrand(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete brand';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-black">Brand Management</h1>
              <p className="text-gray-600 mt-1">Manage tile brands/manufacturers</p>
            </div>
            <button
              onClick={() => {
                setSelectedBrand(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Brand
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search brands..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchBrands}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-accent-gold">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Brands</h3>
            <p className="text-3xl font-bold text-primary-black">{brands.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Showing</h3>
            <p className="text-3xl font-bold text-primary-black">{filteredBrands.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Search Results</h3>
            <p className="text-3xl font-bold text-primary-black">
              {searchTerm ? filteredBrands.length : '-'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No brands found matching your search'
                : 'No brands yet. Add your first brand!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBrands.map((brand) => (
              <CategoryCard
                key={brand.id}
                category={brand}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedBrand(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedBrand}
        title={selectedBrand ? 'Edit Brand' : 'Add New Brand'}
        categoryType="brand"
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedBrand(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedBrand?.name}
        loading={deleteLoading}
      />
    </div>
  );
};

export default BrandsPage;
